<template>
  <div class="bg-white rounded-xl border border-gray-200/60 mb-6 card-shadow">
    <!-- Main toolbar row -->
    <div class="flex items-center h-[52px] px-4 gap-3">
      <!-- Search -->
      <div class="relative flex-1">
        <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input
          :value="search"
          @input="onSearchInput"
          type="text"
          :placeholder="searchPlaceholder"
          class="input-plain w-full h-9 pl-9 pr-3 rounded-lg border-0 bg-transparent text-sm text-gray-900 placeholder-gray-400 outline-none"
        />
      </div>

      <!-- Filter toggle (only if filters slot exists) -->
      <template v-if="hasFilters">
        <div class="w-px h-6 bg-gray-200 shrink-0" />
        <div class="relative" ref="filterWrapper">
          <button
            @click="filtersOpen = !filtersOpen"
            :class="[
              'relative flex items-center justify-center w-9 h-9 rounded-lg transition-colors shrink-0',
              filtersOpen || activeFilterCount > 0
                ? 'bg-[var(--color-primary-light)] text-[var(--color-primary)]'
                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
            ]"
            title="Filters"
          >
            <svg class="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
            </svg>
            <span
              v-if="activeFilterCount > 0"
              class="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[var(--color-primary)] text-white text-[10px] font-bold flex items-center justify-center"
            >
              {{ activeFilterCount }}
            </span>
          </button>

          <!-- Filter dropdown -->
          <Transition name="filter-dropdown">
            <div
              v-if="filtersOpen"
              class="absolute top-full right-0 mt-2 bg-white rounded-xl border border-gray-200 shadow-xl shadow-gray-200/50 z-50 min-w-[240px]"
            >
              <div class="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <span class="text-sm font-semibold text-gray-900">Filters</span>
                <button @click="filtersOpen = false" class="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100">
                  <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
              <div class="p-4 space-y-3 [&>*]:w-full">
                <slot name="filters" />
              </div>
              <div v-if="activeFilterCount > 0" class="px-4 py-3 border-t border-gray-100">
                <button
                  @click="$emit('clear-filters'); filtersOpen = false"
                  class="w-full h-8 rounded-lg text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Clear all filters
                </button>
              </div>
            </div>
          </Transition>
        </div>
      </template>

      <!-- Sort toggle -->
      <template v-if="sortOptions.length">
        <div v-if="!hasFilters" class="w-px h-6 bg-gray-200 shrink-0" />
        <div class="relative" ref="sortWrapper">
          <button
            @click="sortOpen = !sortOpen"
            :class="[
              'relative flex items-center justify-center w-9 h-9 rounded-lg transition-colors shrink-0',
              sortOpen || sort
                ? 'bg-[var(--color-primary-light)] text-[var(--color-primary)]'
                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
            ]"
            title="Sort"
          >
            <svg class="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M4 6h7"/><path d="M4 12h9"/><path d="M4 18h11"/><path d="M18 6v12"/><polyline points="15 15 18 18 21 15"/>
            </svg>
          </button>

          <!-- Sort dropdown -->
          <Transition name="filter-dropdown">
            <div
              v-if="sortOpen"
              class="absolute top-full right-0 mt-2 bg-white rounded-xl border border-gray-200 shadow-xl shadow-gray-200/50 z-50 min-w-[220px]"
            >
              <div class="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <span class="text-sm font-semibold text-gray-900">Sort by</span>
                <button @click="sortOpen = false" class="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100">
                  <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
              <div class="py-1.5">
                <button
                  v-for="option in sortOptions"
                  :key="option.value"
                  @click="selectSort(option.value)"
                  :class="[
                    'w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors',
                    sort === option.value
                      ? 'text-[var(--color-primary)] bg-[var(--color-primary-light)] font-medium'
                      : 'text-gray-600 hover:bg-gray-50'
                  ]"
                >
                  {{ option.label }}
                  <svg v-if="sort === option.value" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </button>
              </div>
              <div v-if="sort" class="px-4 py-3 border-t border-gray-100">
                <button
                  @click="selectSort('')"
                  class="w-full h-8 rounded-lg text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Clear sort
                </button>
              </div>
            </div>
          </Transition>
        </div>
      </template>

      <!-- Pagination -->
      <div class="w-px h-6 bg-gray-200 shrink-0" />
      <div class="flex items-center gap-1.5 shrink-0">
        <button
          @click="$emit('reload')"
          :disabled="total === 0"
          class="flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          title="Reload"
          >
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/>
            </svg>
          </button>
        <span class="text-sm tabular-nums px-1" :class="total > 0 ? 'text-gray-500' : 'text-gray-300'">
          {{ total > 0 ? `${rangeStart}\u2013${rangeEnd} of ${total}` : '0 results' }}
        </span>
        <button
          @click="$emit('page-change', page - 1)"
          :disabled="page <= 1 || total === 0"
          class="flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <button
          @click="$emit('page-change', page + 1)"
          :disabled="page * limit >= total || total === 0"
          class="flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
      </div>

      <!-- Actions (separator only if visible) -->
      <template v-if="hasActions">
        <div class="w-px h-6 bg-gray-200 shrink-0" />
        <div class="flex items-center gap-2 shrink-0">
          <slot name="actions" />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ListToolbar',

  props: {
    search: { type: String, default: '' },
    searchPlaceholder: { type: String, default: 'Search...' },
    total: { type: Number, default: 0 },
    page: { type: Number, default: 1 },
    limit: { type: Number, default: 5 },
    activeFilterCount: { type: Number, default: 0 },
    sort: { type: String, default: '' },
    sortOptions: { type: Array, default: () => [] }
  },

  emits: ['update:search', 'update:sort', 'reload', 'page-change', 'clear-filters'],

  data() {
    return {
      filtersOpen: false,
      sortOpen: false,
      debounceTimer: null
    }
  },

  computed: {
    rangeStart() {
      return (this.page - 1) * this.limit + 1
    },
    rangeEnd() {
      return Math.min(this.page * this.limit, this.total)
    },
    hasFilters() {
      return !!this.$slots.filters
    },
    hasActions() {
      return !!this.$slots.actions
    }
  },

  mounted() {
    document.addEventListener('click', this.onClickOutside)
  },

  beforeUnmount() {
    clearTimeout(this.debounceTimer)
    document.removeEventListener('click', this.onClickOutside)
  },

  methods: {
    onSearchInput(e) {
      clearTimeout(this.debounceTimer)
      const val = e.target.value
      this.debounceTimer = setTimeout(() => {
        this.$emit('update:search', val)
      }, 300)
    },
    selectSort(value) {
      this.$emit('update:sort', value)
      this.sortOpen = false
    },
    onClickOutside(e) {
      if (this.filtersOpen && this.$refs.filterWrapper && !this.$refs.filterWrapper.contains(e.target)) {
        this.filtersOpen = false
      }
      if (this.sortOpen && this.$refs.sortWrapper && !this.$refs.sortWrapper.contains(e.target)) {
        this.sortOpen = false
      }
    }
  }
}
</script>

<style scoped>
.filter-dropdown-enter-active,
.filter-dropdown-leave-active {
  transition: all 0.15s ease;
}
.filter-dropdown-enter-from,
.filter-dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
